import { useEffect, useCallback } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const CONNECTED_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Per-account connection status based on accounts.last_snapshot_at.
 * Connected = last_snapshot_at > now() - 5 minutes.
 */
export const useConnectionStatus = (accountId?: string) => {
  const queryClient = useQueryClient();

  // Realtime subscription for this specific account
  useEffect(() => {
    if (!accountId) return;

    const channel = supabase
      .channel(`conn-status-${accountId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "accounts",
          filter: `id=eq.${accountId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["connection-status", accountId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [accountId, queryClient]);

  const query = useQuery({
    queryKey: ["connection-status", accountId],
    queryFn: async () => {
      if (!accountId) return { status: "disconnected" as const, lastSnapshotAt: null };

      const { data, error } = await supabase
        .from("accounts")
        .select("last_snapshot_at, connection_status")
        .eq("id", accountId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return { status: "disconnected" as const, lastSnapshotAt: null };

      const lastSnapshot = data.last_snapshot_at ? new Date(data.last_snapshot_at) : null;
      const isConnected = lastSnapshot && (Date.now() - lastSnapshot.getTime()) < CONNECTED_THRESHOLD_MS;

      return {
        status: isConnected ? ("connected" as const) : ("disconnected" as const),
        lastSnapshotAt: lastSnapshot,
      };
    },
    enabled: !!accountId,
    refetchInterval: 30_000,
  });

  return {
    status: query.data?.status ?? "disconnected",
    lastSnapshotAt: query.data?.lastSnapshotAt ?? null,
    isLoading: query.isLoading,
    refresh: () => queryClient.invalidateQueries({ queryKey: ["connection-status", accountId] }),
  };
};
